import React, { useMemo } from "react";
import { Tag, Tooltip, Button, theme } from "antd";
import { EyeOutlined, SettingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useResizableColumns } from "../../Common/Custom Components/Ant design/useResizableColumns";
import { DailyPracticeRecord, SortState } from "./types";
import { formatCurrency, formatCount } from "./practiceUtils";

export interface UsePracticeColumnsOptions {
  currentPage: number;
  pageSize: number;
  currentSort: SortState;
  onViewQuickSummary: (record: DailyPracticeRecord) => void;
  onManagePractice?: (record: DailyPracticeRecord) => void;
}

export const usePracticeColumns = ({
  currentPage,
  pageSize,
  currentSort,
  onViewQuickSummary,
  onManagePractice,
}: UsePracticeColumnsOptions) => {
  const { token } = theme.useToken();

  const rawColumns = useMemo(() => {
    return [
      // Fixed Left Columns
      {
        title: "S.No",
        key: "sno",
        width: 55,
        toggleable: false,
        fixed: "left" as const,
        align: "center" as const,
        render: (_: any, __: any, index: number) => (
          <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
            {(currentPage - 1) * pageSize + index + 1}
          </span>
        ),
      },
      {
        title: "Tenant Id",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "practiceId",
        key: "practiceId",
        fixed: "left" as const,
        width: 110,
        align: "center" as const,
        sortOrder:
          currentSort.sortCol === "practiceId"
            ? currentSort.sortDir
            : undefined,
        toggleable: false,
        render: (id: number) => (
          <Tag
            bordered={false}
            color="blue"
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 4,
              padding: "0 6px",
            }}
          >
            #{id}
          </Tag>
        ),
      },
      {
        title: "Practice Name",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "practiceName",
        key: "practiceName",
        width: 240,
        fixed: "left" as const,
        sortOrder:
          currentSort.sortCol === "practiceName"
            ? currentSort.sortDir
            : undefined,
        toggleable: false,
        render: (text: string, record: DailyPracticeRecord) => (
          <Link
            to={`/practices/${record.practiceId}`}
            state={{ practiceName: record.practiceName }}
            style={{
              fontWeight: 500,
              color: token.colorPrimary,
              display: "inline-block",
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
            }}
            title={text}
          >
            {text}
          </Link>
        ),
      },

      // ─── Group 1: Expenses ───────────────────────────────────────────
      {
        title: "AML",
        group: "Expenses",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "aml"],
        key: "aml",
        width: 80,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "AML-Biometric",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "amL_Biometric"],
        key: "amL_Biometric",
        width: 130,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Incoming",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "incoming"],
        key: "incoming",
        width: 110,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Outgoing",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "outgoing"],
        key: "outgoing",
        width: 110,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Emails",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "emails"],
        key: "emails",
        width: 95,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Storage Uses",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "storageUses"],
        key: "storageUses",
        width: 115,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },

      // ─── Group 2: Uses ───────────────────────────────────────────────
      {
        title: "CS",
        group: "Uses",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "cs"],
        key: "cs",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "CF",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "cf"],
        key: "cf",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Accounts",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "accountsReport"],
        key: "accountsReport",
        width: 95,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "CT",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "ct"],
        key: "ct",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "VAT",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "vat"],
        key: "vat",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "EPS",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "eps"],
        key: "eps",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "FPS",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "fps"],
        key: "fps",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "CIS",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "cis"],
        key: "cis",
        width: 75,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "SA100",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "sA100"],
        key: "sA100",
        width: 85,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "SA800",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "sA800"],
        key: "sA800",
        width: 85,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "SA900",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "sA900"],
        key: "sA900",
        width: 85,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },

      // ─── Group 3: Financial ──────────────────────────────────────────
      {
        title: "Active Clients",
        group: "Financial",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "activeClients"],
        key: "activeClients",
        width: 115,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Stripe Count",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "stripeCount"],
        key: "stripeCount",
        width: 110,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Stripe Amt.",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "stripeTotalAmount"],
        key: "stripeTotalAmount",
        width: 135,
        align: "right" as const,
        render: (val: number) => formatCurrency(val, token.colorTextQuaternary),
      },
      {
        title: "GoCardless Count",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "goCardlessCount"],
        key: "goCardlessCount",
        width: 135,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "GoCardless Amt.",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "goCardlessTotalAmount"],
        key: "goCardlessTotalAmount",
        width: 140,
        align: "right" as const,
        render: (val: number) => formatCurrency(val, token.colorTextQuaternary),
      },
      {
        title: "Invoices Count",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "invoicesCount"],
        key: "invoicesCount",
        width: 120,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Invoices Amt.",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "invoicesAmount"],
        key: "invoicesAmount",
        width: 135,
        align: "right" as const,
        render: (val: number) => formatCurrency(val, token.colorTextQuaternary),
      },
      {
        title: "Receipts Count",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "receiptsCount"],
        key: "receiptsCount",
        width: 120,
        align: "center" as const,
        render: (val: number) => formatCount(val, token.colorTextQuaternary),
      },
      {
        title: "Receipts Amt.",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: ["stats", "receiptsAmount"],
        key: "receiptsAmount",
        width: 135,
        align: "right" as const,
        render: (val: number) => formatCurrency(val, token.colorTextQuaternary),
      },

      // Fixed Right Actions Column
      {
        title: "Actions",
        key: "actions",
        width: 80,
        fixed: "right" as const,
        align: "center" as const,
        toggleable: false,
        render: (_: any, record: DailyPracticeRecord) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <Tooltip title="Quick View Summary">
              <Button
                type="text"
                size="small"
                icon={
                  <EyeOutlined
                    style={{ fontSize: 13, color: token.colorPrimary }}
                  />
                }
                onClick={() => onViewQuickSummary(record)}
              />
            </Tooltip>
            {onManagePractice && (
              <Tooltip title="Manage Practice">
                <Button
                  type="text"
                  size="small"
                  icon={
                    <SettingOutlined
                      style={{ fontSize: 13, color: token.colorTextSecondary }}
                    />
                  }
                  onClick={() => onManagePractice(record)}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ];
  }, [
    currentPage,
    pageSize,
    currentSort,
    token,
    onViewQuickSummary,
    onManagePractice,
  ]);

  const { columns, components, columnSelector } = useResizableColumns({
    columns: rawColumns,
    storageKey: "ao_practices_dashboard_bifurcated",
  });

  return {
    columns,
    components,
    columnSelector,
  };
};

export default usePracticeColumns;
